import React, { FunctionComponent, useEffect, useState } from "react";
import Icon from "@mdi/react";
import {
  mdiChevronDoubleLeft,
  mdiChevronDoubleRight,
  mdiChevronLeft,
  mdiChevronRight,
} from "@mdi/js";
interface PaginationProps {
  page: number;
  total: number;
  count: number;
  onChange: (page: number) => void;

  itemCount: number;

  range?: number;
  showFirstLast?: boolean;
  showPrevNext?: boolean;
  showEllipsis?: boolean;
  showMidpoint?: boolean;
}

const Pagination: FunctionComponent<PaginationProps> = ({
  total = 0,
  count = 10,
  page = 1,
  range = 2,
  showEllipsis = true,
  showFirstLast = true,
  showMidpoint = true,
  showPrevNext = true,
  onChange,
  itemCount,
}) => {
  const [start, setStart] = useState(0);
  const [end, setEnd] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [pageBtns, setPageBtns] = useState<number[]>([]);

  const safeTotalPages = Math.max(1, Math.ceil(total / count || 1));
  const safePage = Math.min(Math.max(page || 1, 1), safeTotalPages);

  useEffect(() => {
    const s = total === 0 ? 0 : (safePage - 1) * count + 1;
    setStart(s);

    const e = total === 0 ? 0 : Math.min(total, safePage * count);
    setEnd(e);

    setTotalPages(safeTotalPages);

    if (page !== safePage) onChange(safePage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, total, count]);

  useEffect(() => {
    const t = getPages();
    setPageBtns(t);
  }, [total, totalPages, safePage]);

  function goToPage(p: number) {
    if (p >= 1 && p <= safeTotalPages) onChange(p);
  }

  function getPages() {
    const pages: number[] = [];
    for (let i = safePage - range; i <= safePage + range; i++) {
      if (i >= 1 && i <= safeTotalPages) {
        pages.push(i);
      }
    }

    if (showMidpoint) {
      const remaining = safeTotalPages - safePage;
      const midpoint = safePage + Math.ceil(remaining / 2);
      if (midpoint - safePage > range + 1) {
        pages.push(midpoint);
      }
    }
    return pages;
  }

  return (
    <div className="flex flex-col sm:flex-row justify-between py-2 items-center flex-wrap w-full">
      <div>
        <p className="text-xs text-gray-700">
          Showing <span className="font-medium">{start}</span> to{" "}
          <span className="font-medium">{end}</span> out of{" "}
          <span className="font-medium">{total}</span> Records
        </p>
      </div>

      <div className="pagination flex text-sm rounded-md bg-white">
        {showFirstLast && safePage > range + 1 && (
          <button
            title="First"
            className="pagination-item border rounded-l-md p-2"
            onClick={() => goToPage(1)}
          >
            <Icon path={mdiChevronDoubleLeft} size={0.8} />
          </button>
        )}

        {showPrevNext && safePage > 1 && (
          <button
            title="Previous"
            className={`pagination-item border p-2 ${
              !(showFirstLast && safePage > range + 1) && "rounded-l-md"
            }`}
            onClick={() => goToPage(safePage - 1)}
          >
            <Icon path={mdiChevronLeft} size={0.8} />
          </button>
        )}

        {pageBtns.map((pageBtn, i) => (
          <React.Fragment key={`page-btn-${pageBtn}-${i}`}>
            <button
              className={`pagination-item px-2 py-1 border ${
                pageBtn === safePage
                  ? "active bg-black border-black text-white"
                  : ""
              }`}
              onClick={() => goToPage(pageBtn)}
            >
              {pageBtn}
            </button>
          </React.Fragment>
        ))}

        {showMidpoint && safePage + range < totalPages && (
          <>
            <div className="pagination-item border px-2 py-1">...</div>
            <button
              className={`pagination-item border px-2 py-1 ${
                safePage === totalPages ? "active bg-black text-white" : ""
              }`}
              onClick={() => goToPage(totalPages)}
            >
              {totalPages}
            </button>
          </>
        )}

        {showPrevNext && safePage < totalPages && (
          <button
            title="Next"
            className={`pagination-item border p-2 ${
              !(showFirstLast && safePage < totalPages - range) &&
              "rounded-r-md"
            }`}
            onClick={() => goToPage(safePage + 1)}
          >
            <Icon path={mdiChevronRight} size={0.8} />
          </button>
        )}

        {showFirstLast && safePage < totalPages - range && (
          <button
            title="Last"
            className="pagination-item border p-2 rounded-r-md"
            onClick={() => goToPage(totalPages)}
          >
            <Icon path={mdiChevronDoubleRight} size={0.8} />
          </button>
        )}
      </div>
    </div>
  );
};
export default Pagination;
